import React from "react";
import {useTranslation} from "react-i18next";
import ResumeInfoCard from "../../../../../components/ResumeInfoCard";
import CustomTooltip from "../../../../../components/CustomTooltip";
import {
    DocumentationFolderIcon,
    IconEmailChip,
    FolderPerson,
    IconGuardsTitle,
    IconAreaTable,
    IconPersonOutline,
    IconWeb,
    IconZoneDevices,
    IconMobileChip
} from "../../../../../components/buttons/IconButtons";
import {EMPTY_VALUES} from "../../../../../Apis/functions";
import {summaryHasOverflow, summaryHiddenItems, summaryVisibleItems} from "../../Model/Model";
import SummaryOverflowItem from "../../SummaryOverflowItem";

/**
 * Item of a summary section: its title on the left and its details on the right.
 */
const SummaryItem = ({title, children, className = 'background-shades-50'}) => (
    <div className={`d-flex align-items-center justify-content-between border-radius-8px py-12px px-16px ${className}`}>
        <div className={"flex-grow-1 minw-0 mr-2"}>
            <CustomTooltip
                text={title}
                classes={"fs-14px ibl-font-w-600 font-color-general-950 text-ellipsis-1-row"}
                showIfTruncated={true}
            />
        </div>
        <div className={"d-flex align-items-center justify-content-end"}>
            {children}
        </div>
    </div>
);

/**
 * Icon with a text, the details of a summary item.
 */
const SummaryItemDetail = ({Icon, text, className = ''}) => (
    <div className={`d-flex align-items-center minw-0 ${className}`}>
        <Icon className={"mr-8px"}/>
        <div className={"minw-0"}>
            <CustomTooltip
                text={text}
                classes={"fs-14px font-color-shades-800 text-ellipsis-1-row"}
                showIfTruncated={true}
            />
        </div>
    </div>
);



/**
 * Review of the zone access before it is created: its name, its zones and its guards.
 *
 * @param {string} name Name of the zone access.
 * @param {Array} selectedZones Zones picked, with their name and, when known, their devices count.
 * @param {Array} selectedGuards Guards picked, with their full name, email and phone number.
 */
const SummaryStep = ({name, selectedZones, selectedGuards}) => {
    const {t} = useTranslation();

    return (
        <div className={"mt-4"}>
            <div className={"mb-4"}>
                <ResumeInfoCard
                    title={t('details')}
                    titleIcon={<DocumentationFolderIcon width={24} height={24}/>}
                    icon={<FolderPerson/>}
                    iconClass={'background-primary-100'}
                    contentClass={"py-3"}
                    body={
                        <div className={"d-flex flex-column gap-12px background-shades-50 border-radius-8px py-12px px-16px"}>
                            <p className={"fs-14px ibl-font-w-600 font-color-general-950"}>{t('name')}</p>
                            <p className={"fs-14px font-color-shades-800 text-ellipsis-1-row"}>{name || EMPTY_VALUES.DEFAULT}</p>
                        </div>
                    }
                />
            </div>

            <div className={"mb-4"}>
                <ResumeInfoCard
                    title={t('zones')}
                    titleIcon={<IconAreaTable/>}
                    icon={<IconWeb/>}
                    iconClass={'background-primary-100'}
                    contentClass={"py-3"}
                    body={
                        <div className={"grid-container-resume-data"}>
                            {summaryVisibleItems(selectedZones).map((zone) => (
                                <SummaryItem key={zone.id} title={zone.name}>
                                    {zone.devicesCount !== undefined &&
                                        <SummaryItemDetail
                                            Icon={IconZoneDevices}
                                            text={`${zone.devicesCount} ${t('devices')}`}
                                        />
                                    }
                                </SummaryItem>
                            ))}
                            {summaryHasOverflow(selectedZones) &&
                                <SummaryOverflowItem label={'total_added_zones'} hiddenItems={summaryHiddenItems(selectedZones).map((zone) => ({label: zone.name, value: zone.devicesCount !== undefined ? `${zone.devicesCount} ${t('devices')}` : ''}))}/>
                            }
                        </div>
                    }
                />
            </div>

            <div className={"mb-4"}>
                <ResumeInfoCard
                    title={t('guards')}
                    titleIcon={<IconGuardsTitle/>}
                    icon={<IconPersonOutline/>}
                    iconClass={'background-primary-100'}
                    contentClass={"py-3"}
                    body={
                        <div className={"grid-container-resume-data"}>
                            {summaryVisibleItems(selectedGuards).map((guard) => (
                                <SummaryItem key={guard.userId} title={guard.fullName}>
                                    <SummaryItemDetail Icon={IconEmailChip} text={guard.email || EMPTY_VALUES.DEFAULT} className={"w-163px"}/>
                                    <SummaryItemDetail Icon={IconMobileChip} text={guard.phoneNumber || EMPTY_VALUES.DEFAULT} className={"w-144px ml-1"}/>
                                </SummaryItem>
                            ))}
                            {summaryHasOverflow(selectedGuards) &&
                                <SummaryOverflowItem label={'total_added_users'} hiddenItems={summaryHiddenItems(selectedGuards).map((guard) => ({label: guard.fullName, value: guard.email || ''}))}/>
                            }
                        </div>
                    }
                />
            </div>
        </div>
    );
};

export default SummaryStep;
